import React, { useEffect, useMemo, useState } from "react";

import { Button, Confirm, Icon, Modal } from "semantic-ui-react";
import { useIntl } from "react-intl";

import {
  ACTIONS,
  BTN_ACTS,
  EDUCATION_LEVEL_OPTIONS,
  OnEvent,
  PickedState,
  SEQUENCE_NAME,
  SetProp,
  EducationDataType,
} from "./sequence/EmployeeInfo";

import ModEmployeeEducation from "./ModEmployeeEducation";
import ModEmployeePendingAlert from "./ModEmployeePendingAlert";

type CardEducationInformationProps = Pick<
  PickedState,
  "buttonLoadCheck" | "changePassword" | "errorMessage" | "successMessage"
> & {
  setProp: SetProp;
  employeeId?: number;
  eduCategoryOption?: Record<string, any>[];
  education?: EducationType[];
  birthDate?: string | null;
  deleteEducationData?: (id: number) => void;
  saveEducationData?: (
    educationData: EducationDataType,
    callback?: (result: { success: boolean }) => void
  ) => void;
  languageUX: "en" | "th";
};

type EducationType = {
  education_level?: string;
  educationLevel?: string;
  id?: number | null;
  tempId?: string;
  institution?: string;
  faculty?: string;
};

const CardEducationInformation = (props: CardEducationInformationProps) => {
  const intl = useIntl();

  const [showEducationInfoModal, setShowEducationInfoModal] = useState(false);
  const [showPendingAlert, setShowPendingAlert] = useState(false);
  const [editingEducation, setEditingEducation] = useState<any>(null);
  const [pendingEducation, setPendingEducation] = useState<EducationDataType[]>([]);
  const [deleteEducationId, setDeleteEducationId] = useState<number | null>(null);
  const [showDeleteEducationConfirm, setShowDeleteEducationConfirm] = useState(false);
  const [isSavingEducation, setIsSavingEducation] = useState(false);
  const [hasShownPendingAlert, setHasShownPendingAlert] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    const error = props.errorMessage?.[BTN_ACTS.SAVE];

    if (!error) return;

    props.setProp(`errorMessage.${SEQUENCE_NAME}`, error);
    setShowErrorModal(true);
  }, [props.errorMessage?.[BTN_ACTS.SAVE]]);

  useEffect(() => {
    const saveStatus = props.buttonLoadCheck?.[BTN_ACTS.EDUCATION_SAVE];

    if (!isSavingEducation || saveStatus !== "SUCCESS") return;

    setIsSavingEducation(false);
    setShowEducationInfoModal(false);
    setEditingEducation(null);
  }, [isSavingEducation, props.buttonLoadCheck?.[BTN_ACTS.EDUCATION_SAVE]]);

  const educationCategoryOptions = useMemo(() => {
    const items: Record<string, any>[] = props.eduCategoryOption || [];

    return items.map((item) => ({
      key: item.id,
      text: item.name,
      value: item.id,
    }));
  }, [props.eduCategoryOption]);

  const educationInfoList = useMemo(
    () => [
      ...(props.education?.map((edu, index) => ({
        ...edu,
        displayIndex: index + 1,
        isPending: false,
      })) ?? []),
      ...pendingEducation.map((edu, index) => ({
        ...edu,
        displayIndex: (props.education?.length ?? 0) + index + 1,
        isPending: true,
      })),
    ],
    [pendingEducation, props.education]
  );

  const handleSaveEducation = (educationData: EducationDataType) => {
    if (props.employeeId) {
      setIsSavingEducation(true);
      props.saveEducationData?.(educationData, (result) => {
        if (result.success) {
          setIsSavingEducation(false);
          setShowEducationInfoModal(false);
          setEditingEducation(null);
        }
      });
      return;
    }

    if (educationData.tempId) {
      setPendingEducation((prev) =>
        prev.map((p) => (p.tempId === educationData.tempId ? { ...educationData } : p))
      );
    } else {
      setPendingEducation((prev) => [...prev, { ...educationData, tempId: Date.now() }]);
    }

    setShowEducationInfoModal(false);
    setEditingEducation(null);
  };

  const handleShowEducationInfo = () => {
    if (props.employeeId || hasShownPendingAlert) {
      setEditingEducation(null);
      setShowEducationInfoModal(true);
      return;
    }

    setShowPendingAlert(true);
    setHasShownPendingAlert(true);
  };

  return (
    <div id="CardEducationInformation">
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          id="CardEducationInformation"
          style={{
            color: "rgba(26, 115, 232, 1)",
            fontSize: "0.95rem",
            margin: "1rem 0 0.5rem",
            fontWeight: "bold",
            minWidth: "200px",
          }}
        >
          {props.languageUX === "en" ? "Education Information" : "ข้อมูลการศึกษา"}
        </div>

        <Button
          color="blue"
          onClick={handleShowEducationInfo}
          size="small"
          style={{ padding: "8px 48px" }}
        >
          {props.languageUX === "en" ? "Add" : "เพิ่ม"}
        </Button>
      </div>

      <div>
        {educationInfoList.map((edu) => {
          const levelText =
            EDUCATION_LEVEL_OPTIONS.find(
              (item) => item.value === edu.education_level || item.value === edu.educationLevel
            )?.text || edu.education_level;

          return (
            <div
              key={edu.id ?? edu.tempId}
              style={{
                alignItems: "center",
                backgroundColor: "rgba(254, 245, 245, 1)",
                borderRadius: "8px",
                display: "flex",
                fontWeight: "bold",
                justifyContent: "space-between",
                marginBottom: "1rem",
                padding: "1rem",
              }}
            >
              <div>
                ลำดับที่ {edu.displayIndex} : {levelText} {edu.institution} {edu.faculty}
              </div>

              <div style={{ display: "flex", gap: "12px", marginRight: "32px" }}>
                <Icon
                  className="edit"
                  style={{ color: "gray", cursor: "pointer" }}
                  onClick={() => {
                    setEditingEducation({
                      ...edu,
                      index: edu.displayIndex,
                    });
                    setShowEducationInfoModal(true);
                  }}
                />
                <Icon
                  className="trash alternate outline"
                  style={{ color: "gray", cursor: "pointer" }}
                  onClick={() => {
                    if (edu.isPending) {
                      setPendingEducation((prev) => prev.filter((p) => p.tempId !== edu.tempId));
                    } else {
                      setDeleteEducationId(edu.id ?? null);
                      setShowDeleteEducationConfirm(true);
                    }
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <ModEmployeePendingAlert
        open={showPendingAlert}
        onCancel={() => setShowPendingAlert(false)}
        onConfirm={() => {
          setShowPendingAlert(false);
          setEditingEducation(null);
          setShowEducationInfoModal(true);
        }}
      />

      <ModEmployeeEducation
        setProp={props.setProp}
        birthDate={props.birthDate}
        educationLength={props.education?.length ?? 0}
        initialData={editingEducation}
        open={showEducationInfoModal}
        pendingEducationLength={pendingEducation.length}
        buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.EDUCATION_SAVE]}
        languageUX={props.languageUX}
        educationCategoryOptions={educationCategoryOptions}
        onClose={() => {
          setShowEducationInfoModal(false);
          setEditingEducation(null);
        }}
        onSave={handleSaveEducation}
      />

      <Confirm
        header="ยืนยันการลบข้อมูลการศึกษา"
        open={showDeleteEducationConfirm}
        onCancel={() => {
          setShowDeleteEducationConfirm(false);
          setDeleteEducationId(null);
        }}
        onConfirm={() => {
          if (deleteEducationId) {
            props.deleteEducationData?.(deleteEducationId);
          }
          setShowDeleteEducationConfirm(false);
          setDeleteEducationId(null);
        }}
      />

      {showErrorModal && (
        <Modal
          open={showErrorModal}
          onClose={() => {
            setShowErrorModal(false);
            props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
          }}
        >
          <Modal.Content style={{ textAlign: "center" }}>
            <div style={{ fontWeight: "bold" }}>
              {(() => {
                const err = props.errorMessage?.[SEQUENCE_NAME];

                if (!err) return "Unknown error";

                if (typeof err === "string") return <div>{err}</div>;

                return Object.entries(err).flatMap(([key, messages]) =>
                  (messages as string[]).map((message, i) => (
                    <div key={`${key}-${i}`}>
                      {key}: {message}
                    </div>
                  ))
                );
              })()}
            </div>
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
};

export default React.memo(CardEducationInformation);
